/** Browser client for the research Worker's JSON API (see domain/api.ts). */
import { ApiError, requestJson } from "@bodemleven/course-kit/client";
import type { ResearchApi, UploadFile } from "./domain/api.ts";
import { UPLOAD_PARTS } from "./domain/imaging.ts";

const send = <T>(method: string, path: string, json?: unknown): Promise<T> => {
  const headers: Record<string, string> = { accept: "application/json" };
  if (json !== undefined) headers["content-type"] = "application/json";
  return requestJson<T>(path, {
    method,
    headers,
    body: json === undefined ? undefined : JSON.stringify(json),
  });
};

const id = (value: string): string => encodeURIComponent(value);

const uploadForm = (files: readonly UploadFile[]): FormData => {
  const form = new FormData();
  form.append(UPLOAD_PARTS.manifest, JSON.stringify(files.map((file) => file.item)));
  files.forEach((file, index) => {
    form.append(UPLOAD_PARTS.file(index), file.original, file.item.name);
    if (file.view) form.append(UPLOAD_PARTS.view(index), file.view);
  });
  return form;
};

/** Photo bytes for <img>/<video>, served outside /api with the analyst session cookie. */
export const photoUrl = (photoId: string, which: "view" | "original"): string =>
  `/photos/${id(photoId)}/${which}`;

/** Same-origin: the Worker serves both the SPA and /api. */
export const researchApi: ResearchApi = {
  listLands: () => send("GET", "/api/lands"),
  createLand: (input) => send("POST", "/api/lands", input),
  getLand: (landId) => send("GET", `/api/lands/${id(landId)}`),
  updateLand: (landId, input) => send("PUT", `/api/lands/${id(landId)}`, input),
  deleteLand: (landId) => send("DELETE", `/api/lands/${id(landId)}`),

  listSamples: () => send("GET", "/api/samples"),
  createSample: (input) => send("POST", "/api/samples", input),
  getSample: (sampleId) => send("GET", `/api/samples/${id(sampleId)}`),
  updateSample: (sampleId, input) => send("PUT", `/api/samples/${id(sampleId)}`, input),
  deleteSample: (sampleId) => send("DELETE", `/api/samples/${id(sampleId)}`),
  sendSample: (sampleId) => send("POST", `/api/samples/${id(sampleId)}/send`, {}),

  listResearch: () => send("GET", "/api/research"),
  getResearch: (researchId) => send("GET", `/api/research/${id(researchId)}`),

  listAdvice: () => send("GET", "/api/advice"),
  getAdvice: (adviceId) => send("GET", `/api/advice/${id(adviceId)}`),

  admin: {
    listUsers: () => send("GET", "/api/admin/users"),
    restoreLand: (landId) => send("POST", `/api/admin/lands/${id(landId)}/restore`, {}),
    receiveSample: (sampleId) => send("POST", `/api/admin/samples/${id(sampleId)}/receive`, {}),
    createResearch: (input) => send("POST", "/api/admin/research", input),
    updateResearch: (researchId, input) =>
      send("PUT", `/api/admin/research/${id(researchId)}`, input),
    rescoreResearch: (researchId, input) =>
      send("POST", `/api/admin/research/${id(researchId)}/rescore`, input),
    deleteResearch: (researchId) => send("DELETE", `/api/admin/research/${id(researchId)}`),
    createAdvice: (input) => send("POST", "/api/admin/advice", input),
    updateAdvice: (adviceId, input) => send("PUT", `/api/admin/advice/${id(adviceId)}`, input),
    deleteAdvice: (adviceId) => send("DELETE", `/api/admin/advice/${id(adviceId)}`),

    listCalibrations: () => send("GET", "/api/admin/calibrations"),
    createCalibration: (input) => send("POST", "/api/admin/calibrations", input),
    retireCalibration: (calibrationId) =>
      send("POST", `/api/admin/calibrations/${id(calibrationId)}/retire`, {}),

    getCapture: (researchId) => send("GET", `/api/admin/research/${id(researchId)}/capture`),
    referenceQueue: () => send("GET", "/api/admin/reference/queue"),
    uploadReferencePhotos: (input, files) => {
      const body = uploadForm(files);
      body.set("group", input.group);
      body.set("magnification", String(input.magnification));
      if (input.dilution !== null) body.set("dilution", String(input.dilution));
      return requestJson("/api/admin/reference/photos", {
        method: "POST",
        headers: { accept: "application/json" },
        body,
      });
    },
    startSession: (researchId, input) =>
      send("POST", `/api/admin/research/${id(researchId)}/sessions`, input),
    closeSession: (sessionId) => send("POST", `/api/admin/sessions/${id(sessionId)}/close`, {}),
    // No content-type: the browser sets the multipart boundary itself.
    uploadPhotos: (sessionId, files) =>
      requestJson(`/api/admin/sessions/${id(sessionId)}/photos`, {
        method: "POST",
        headers: { accept: "application/json" },
        body: uploadForm(files),
      }),
    setPhotoQuality: (photoId, input) =>
      send("PUT", `/api/admin/photos/${id(photoId)}/quality`, input),
    setPhotoDilution: (photoId, input) =>
      send("PUT", `/api/admin/photos/${id(photoId)}/dilution`, input),

    listObjects: (researchId) => send("GET", `/api/admin/research/${id(researchId)}/objects`),
    saveObjects: (researchId, input) =>
      send("POST", `/api/admin/research/${id(researchId)}/objects`, input),
    setReferenceSet: (researchId, input) =>
      send("POST", `/api/admin/research/${id(researchId)}/reference-set`, input),

    claimJobs: (researchId, input) =>
      send("POST", `/api/admin/research/${id(researchId)}/jobs/claim`, input),
    postJobResult: (jobId, input) => send("POST", `/api/admin/jobs/${id(jobId)}/result`, input),
    failJob: (jobId, input) => send("POST", `/api/admin/jobs/${id(jobId)}/fail`, input),
    retryJob: (jobId) => send("POST", `/api/admin/jobs/${id(jobId)}/retry`, {}),

    listRunnerKeys: () => send("GET", "/api/admin/runner-keys"),
    createRunnerKey: (input) => send("POST", "/api/admin/runner-keys", input),
    updateRunnerKey: (keyId, input) => send("PUT", `/api/admin/runner-keys/${id(keyId)}`, input),
    revokeRunnerKey: (keyId) => send("POST", `/api/admin/runner-keys/${id(keyId)}/revoke`, {}),

    listModelVersions: () => send("GET", "/api/admin/model-versions"),
    createModelVersion: (input) => send("POST", "/api/admin/model-versions", input),
    updateModelVersion: (versionId, input) =>
      send("PUT", `/api/admin/model-versions/${id(versionId)}`, input),
    startBenchmark: (input) => send("POST", "/api/admin/benchmarks", input),
    getBenchmark: (group) => send("GET", `/api/admin/benchmarks?group=${id(group)}`),
    gateBenchmark: (input) => send("POST", "/api/admin/benchmarks/gate", input),

    getPipeline: () => send("GET", "/api/admin/pipeline"),
    createPipelineStep: (input) => send("POST", "/api/admin/pipeline/steps", input),
    updatePipelineStep: (stepId, input) =>
      send("PUT", `/api/admin/pipeline/steps/${id(stepId)}`, input),
    evaluatePipelineStep: (stepId) =>
      send("POST", `/api/admin/pipeline/steps/${id(stepId)}/evaluate`, {}),
    gatePipelineStep: (stepId) => send("POST", `/api/admin/pipeline/steps/${id(stepId)}/gate`, {}),
    setAutonomy: (group, input) => send("PUT", `/api/admin/autonomy/${id(group)}`, input),
    getAgreement: (group) => send("GET", `/api/admin/reference/agreement?group=${id(group)}`),
  },
};

export const isNotFound = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 404;
